// Iter 133c — Legacy specialty audit & migration modal.
// PM clicks "Audit specialty codes" in the export menu → this opens.
// 1. Auto-loads the audit on mount.
// 2. Shows: total legacy practitioners, empty-specialty count, per-code breakdown.
// 3. PM can hit "Migrate now" to rewrite all legacy codes to their Kestral
//    equivalent. Empty specialties are NOT auto-filled (that'd be a guess).
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldCheck, AlertTriangle, CheckCircle2, X, Loader2, Wand2, Sparkles } from 'lucide-react';
import AISpecialtyProposeModal from './AISpecialtyProposeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LegacySpecialtyAuditModal({ onClose, onMigrated }) {
  const [audit, setAudit] = useState(null);
  const [autoFillPreview, setAutoFillPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [result, setResult] = useState(null);
  const [autoFillResult, setAutoFillResult] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [a, pv] = await Promise.all([
        axios.get(`${API}/referrals/legacy-specialties/audit`, { withCredentials: true }),
        axios.get(`${API}/referrals/specialty-from-category/preview`, { withCredentials: true }),
      ]);
      setAudit(a.data);
      setAutoFillPreview(pv.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load audit');
    }
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const migrate = async () => {
    if (!window.confirm(
      `Rewrite ${audit.total_legacy_practitioners} practitioner specialty value(s) to their Kestral-accepted code?\n\n` +
      `Empty specialties are left as-is — they'd still default to GeneralMedicine in the export.`
    )) return;
    setMigrating(true);
    try {
      const r = await axios.post(`${API}/referrals/legacy-specialties/migrate`, {}, { withCredentials: true });
      setResult(r.data);
      await reload();
      onMigrated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Migration failed');
    }
    setMigrating(false);
  };

  const autoFill = async () => {
    const needsReview = autoFillPreview.by_category.filter(c => c.needs_review)
                          .reduce((s, c) => s + c.count, 0);
    const msg =
      `Auto-fill ${autoFillPreview.would_update} empty specialty value(s) using each clinic's category?\n\n` +
      (needsReview > 0
        ? `${needsReview} record(s) come from "Specialist" or "Other" categories — they'll be set to GeneralMedicine and should be reviewed manually afterwards.`
        : 'All categories map confidently to a Kestral specialty.');
    if (!window.confirm(msg)) return;
    setAutoFilling(true);
    try {
      const r = await axios.post(`${API}/referrals/specialty-from-category/apply`, {},
        { withCredentials: true });
      setAutoFillResult(r.data);
      await reload();
      onMigrated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Auto-fill failed');
    }
    setAutoFilling(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="legacy-specialty-audit-modal"
           className="bg-card rounded-xl border max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-3.5 border-b flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-[#0d2644]/10 text-[#0d2644] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: '#0d2644' }}>Specialty-code audit</h2>
              <div className="text-xs text-muted-foreground">Find &amp; migrate non-Kestral specialty codes</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" data-testid="legacy-audit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {loading ? (
            <div className="py-10 flex flex-col items-center text-muted-foreground text-sm">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              Scanning every practitioner in the Referral Base…
            </div>
          ) : err ? (
            <div className="rounded-lg p-3 bg-red-50 border border-red-200 text-sm text-red-700">{err}</div>
          ) : audit ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border bg-muted/30 p-3" data-testid="legacy-count-bad">
                  <div className="text-3xl font-extrabold" style={{ color: audit.total_legacy_practitioners ? '#b45309' : '#15803d' }}>
                    {audit.total_legacy_practitioners}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Practitioners on a non-Kestral code</div>
                </div>
                <div className="rounded-lg border bg-muted/30 p-3" data-testid="legacy-count-empty">
                  <div className="text-3xl font-extrabold" style={{ color: audit.empty_specialty_count ? '#b45309' : '#15803d' }}>
                    {audit.empty_specialty_count}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Practitioners with empty specialty
                    <div className="text-[10px] italic mt-0.5">(default to GeneralMedicine in export)</div>
                  </div>
                </div>
              </div>

              {audit.by_code?.length > 0 ? (
                <div className="rounded-lg border overflow-hidden">
                  <div className="bg-muted/40 px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#0d2644]">
                    Distinct legacy codes ({audit.distinct_legacy_codes})
                  </div>
                  <div className="divide-y text-sm max-h-64 overflow-y-auto">
                    {audit.by_code.map(x => (
                      <div key={x.code} className="px-3 py-2 flex items-center gap-2"
                           data-testid={`legacy-code-${x.code}`}>
                        <code className="font-mono text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded">{x.code}</code>
                        <span className="text-muted-foreground">→</span>
                        <code className="font-mono text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded">{x.kestral_target}</code>
                        <span className="ml-auto text-xs font-semibold">{x.count} row{x.count === 1 ? '' : 's'}</span>
                        {!x.in_legacy_map && (
                          <span title="No explicit mapping — defaults to GeneralMedicine"
                            className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            fallback
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : audit.total_legacy_practitioners === 0 ? (
                <div className="rounded-lg border bg-green-50 border-green-200 p-4 text-sm">
                  <div className="font-bold text-green-800 mb-1 inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> All specialty codes are already Kestral-valid.
                  </div>
                  <div className="text-green-700/80 text-xs">
                    Nothing to migrate. Export will be accepted by Kestral without code translation.
                  </div>
                </div>
              ) : null}

              {result && (
                <div className="rounded-lg border bg-blue-50 border-blue-200 p-3" data-testid="legacy-migrate-result">
                  <div className="font-bold text-blue-800 mb-1 inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Migrated {result.practitioners_updated} practitioner(s) across {result.referrers_updated} clinic(s).
                  </div>
                  {result.translations?.length > 0 && (
                    <ul className="text-xs text-blue-700/80 mt-1 space-y-0.5">
                      {result.translations.map(t => (
                        <li key={t.mapping}><code>{t.mapping}</code> · {t.count} rows</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {audit.empty_specialty_count > 0 && autoFillPreview && (
                <div className="rounded-lg border bg-purple-50 border-purple-200 p-4 space-y-3"
                     data-testid="autofill-from-category-panel">
                  <div className="flex items-start gap-2">
                    <Wand2 className="w-5 h-5 text-purple-700 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-bold text-purple-900 text-sm">
                        Auto-fill empties from clinic category
                      </div>
                      <div className="text-xs text-purple-800/80 mt-0.5">
                        Each clinic&apos;s category (GP, Physio, Chiro, Podiatrist…) maps confidently
                        to a Kestral specialty. This will fill all <strong>{autoFillPreview.would_update}</strong> empty
                        practitioner specialties in one click.
                      </div>
                    </div>
                  </div>
                  {autoFillPreview.by_category.length > 0 && (
                    <div className="text-xs space-y-0.5 ml-7" data-testid="autofill-preview-list">
                      {autoFillPreview.by_category.map(c => (
                        <div key={c.category} className="flex items-center gap-2">
                          <code className="bg-white border px-1.5 py-0.5 rounded">{c.category}</code>
                          <span>→</span>
                          <code className={`px-1.5 py-0.5 rounded ${c.needs_review ? 'bg-amber-100 text-amber-900' : 'bg-green-100 text-green-900'}`}>
                            {c.specialty || c.target_specialty}
                          </code>
                          <span className="ml-auto font-semibold">{c.count}</span>
                          {c.needs_review && (
                            <span title="Manual review recommended — category is too generic"
                              className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                              review
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowAI(true)}
                            data-testid="ai-specialty-open"
                            className="text-xs font-semibold px-3.5 py-1.5 rounded-lg bg-purple-100 text-purple-800 border border-purple-300 inline-flex items-center gap-1.5 hover:bg-purple-200">
                      <Sparkles className="w-3.5 h-3.5" /> AI-suggest specialties
                    </button>
                    <button onClick={autoFill} disabled={autoFilling || autoFillPreview.would_update === 0}
                            data-testid="autofill-from-category-btn"
                            className="text-xs font-semibold px-3.5 py-1.5 rounded-lg bg-purple-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50">
                      {autoFilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                      {autoFilling ? 'Filling…' : `Auto-fill ${autoFillPreview.would_update} now`}
                    </button>
                  </div>
                </div>
              )}

              {autoFillResult && (
                <div className="rounded-lg border bg-blue-50 border-blue-200 p-3" data-testid="autofill-result">
                  <div className="font-bold text-blue-800 mb-1 inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Auto-filled {autoFillResult.practitioners_updated} practitioner(s) across {autoFillResult.referrers_updated} clinic(s).
                  </div>
                  <ul className="text-xs text-blue-700/80 mt-1 space-y-0.5">
                    {autoFillResult.by_category.map(c => (
                      <li key={c.category} className="flex items-center gap-2">
                        <code>{c.category}</code> → <code>{c.specialty}</code> · {c.count}
                        {c.needs_review && <span className="text-amber-700">⚠ review</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {audit.empty_specialty_count > 0 && (
                <div className="rounded-lg border bg-amber-50 border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>{audit.empty_specialty_count} practitioner(s) have no specialty set.</strong>{' '}
                    They&apos;ll default to <code>GeneralMedicine</code> in the export.
                    Set a specific specialty per practitioner where appropriate via Edit → Practitioners.
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between gap-2 bg-muted/20">
          <div className="text-[11px] text-muted-foreground">
            Empty specialties are not auto-filled — needs manual assignment.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAI(true)} data-testid="legacy-audit-ai"
                    title="Ask GPT-5.2 to propose better specialties for Specialist / Other clinics"
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-purple-300 text-purple-800 hover:bg-purple-50 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> AI suggest
            </button>
            <button onClick={onClose} data-testid="legacy-audit-cancel"
                    className="text-sm font-semibold px-4 py-1.5 rounded-lg border hover:bg-muted/40">
              Close
            </button>
            <button onClick={migrate}
                    disabled={migrating || loading || (audit?.total_legacy_practitioners ?? 0) === 0}
                    data-testid="legacy-audit-migrate"
                    className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-[#0d2644] text-white inline-flex items-center gap-1.5 disabled:opacity-50">
              {migrating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              {migrating ? 'Migrating…' : `Migrate ${audit?.total_legacy_practitioners ?? 0} record(s)`}
            </button>
          </div>
        </div>
      </div>

      {showAI && (
        <AISpecialtyProposeModal
          onClose={() => setShowAI(false)}
          onApplied={() => { reload(); onMigrated?.(); }} />
      )}
    </div>
  );
}
